import { configureStore, combineReducers } from '@reduxjs/toolkit'
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { apiSlice } from './api/apiSlice'
import { listenerMiddleware } from './listenerMiddleware'
import { stripPersistedAuthToken } from './persistTransforms'

import accountsSliceReducer from './slices/accounting/accountsSlice'
import adminUISliceReducer from './slices/adminUISlice'
import depositsWithdrawalsSliceReducer from './slices/accounting/depositsWithdrawalsSlice'
import draftJournalsSliceReducer from './slices/accounting/draftJournalsSlice'
import enhancedReportingSliceReducer from './slices/accounting/enhancedReportingSlice'
import expensesSliceReducer from './slices/accounting/expensesSlice'
import financialStatementsSliceReducer from './slices/accounting/financialStatementsSlice'
import fixedAssetsSliceReducer from './slices/accounting/fixedAssetsSlice'
import transfersSliceReducer from './slices/accounting/transfersSlice'
import accountingSliceReducer from './slices/accountingSlice'
import accountingWebSocketSliceReducer from './slices/accountingWebSocketSlice'
import agingReportSliceReducer from './slices/agingReportSlice'
import aiReportSliceReducer from './slices/aiReportSlice'
import aiSliceReducer from './slices/aiSlice'
import authSliceReducer from './slices/authSlice'
import compactSidebarSliceReducer from './slices/compactSidebarSlice'
import configSliceReducer from './slices/configSlice'
import favoritesSliceReducer from './slices/favoritesSlice'
import fiscalPeriodSliceReducer from './slices/fiscalPeriodSlice'
import hrmSliceReducer from './slices/hrmSlice'
import inventorySliceReducer from './slices/inventorySlice'
import journalApprovalSliceReducer from './slices/journalApprovalSlice'
import languageSliceReducer from './slices/languageSlice'
import largeSidebarSliceReducer from './slices/largeSidebarSlice'
import notificationsSliceReducer from './slices/notificationsSlice'
import projectSliceReducer from './slices/projectSlice'

import salaryHistorySliceReducer from './slices/salaryHistorySlice'

import settingsSliceReducer from './slices/settingsSlice'
import sidebarSliceReducer from './slices/sidebarSlice'
import stockWebSocketSliceReducer from './slices/stockWebSocketSlice'

import taskSliceReducer from './slices/taskSlice'
import tenantFileSliceReducer from './slices/tenantFileSlice'
import tenantSliceReducer from './slices/tenantSlice'
import uiSliceReducer from './slices/uiSlice'
import posSessionSliceReducer from './slices/posSessionSlice'
import openingInventoryReducer from './slices/openingInventorySlice'
import payrollFoundationReducer from './slices/payrollFoundationSlice'

const rootReducer = combineReducers({
  adminUI: adminUISliceReducer,
  accounts: accountsSliceReducer,
  depositsWithdrawals: depositsWithdrawalsSliceReducer,
  draftJournals: draftJournalsSliceReducer,
  enhancedReporting: enhancedReportingSliceReducer,
  expenses: expensesSliceReducer,
  financialStatements: financialStatementsSliceReducer,
  fixedAssets: fixedAssetsSliceReducer,
  transfers: transfersSliceReducer,
  accounting: accountingSliceReducer,
  accountingWebSocket: accountingWebSocketSliceReducer,
  agingReport: agingReportSliceReducer,
  aiReport: aiReportSliceReducer,
  ai: aiSliceReducer,
  auth: authSliceReducer,
  compactSidebar: compactSidebarSliceReducer,
  config: configSliceReducer,
  favorites: favoritesSliceReducer,
  fiscalPeriod: fiscalPeriodSliceReducer,
  hrm: hrmSliceReducer,
  inventory: inventorySliceReducer,
  journalApproval: journalApprovalSliceReducer,
  language: languageSliceReducer,
  largeSidebar: largeSidebarSliceReducer,
  notifications: notificationsSliceReducer,
  project: projectSliceReducer,

  salaryHistory: salaryHistorySliceReducer,

  settings: settingsSliceReducer,
  sidebar: sidebarSliceReducer,
  stockWebSocket: stockWebSocketSliceReducer,

  task: taskSliceReducer,
  tenantFile: tenantFileSliceReducer,
  tenant: tenantSliceReducer,
  ui: uiSliceReducer,
  posSession: posSessionSliceReducer,
  openingInventory: openingInventoryReducer,































  payrollFoundation: payrollFoundationReducer,

  [apiSlice.reducerPath]: apiSlice.reducer,
})


const persistConfig = {
  key: 'root',
  storage,
  // posSession keeps the open ticket across reloads/token-expiry redirects —
  // losing a cashier's mid-sale cart was a data-loss bug, not a convenience.
  whitelist: ['auth', 'tenant', 'posSession'],
  // tokenStorage is the single source for the access token; never persist a
  // second copy inside the auth slice (see persistTransforms.ts).
  transforms: [stripPersistedAuthToken],
}

const persistedReducer = persistReducer(persistConfig, rootReducer)

export const store = configureStore({
  reducer: persistedReducer as any,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(listenerMiddleware.middleware)
      .concat(apiSlice.middleware),
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
export type AppStore = typeof store
